import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { debounce } from 'lodash-es';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { SellerUserAllSettingsContext } from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext';
import {
  userSettingsFamilyAtom,
  userSettingsQueryKeyAtom,
} from 'src/contexts/SellerUserSettingsContext/SellerUserAllSettingsContext.atom';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CustomQuickFilterOfListingQuery,
  CustomQuickFilterOfMarketplacePaymentQuery,
  CustomQuickFilterOfOnSaleEventQuery,
  CustomQuickFilterOfPurchaseOrderQuery,
  CustomQuickFilterOfSaleQuery,
  Feature,
  SellerUserClient,
  UserSetting,
} from 'src/WebApiController';

import { useUserHasFeature } from './useUserHasFeature';

const useRealTimeUserSetting = <T>(
  prefetchQueryKey: (string | boolean | UserSetting[] | undefined)[],
  contextIsReady: boolean,
  id?: UserSetting,
  currentLoginUserOnly?: boolean,
  isSettingPrefetched?: (id: UserSetting) => boolean,
  prefetchRefresh?: () => void
) => {
  const { showErrorDialog, trackError } = useClientLogContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const sellerUserClient = useMemo(
    () => new SellerUserClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );
  const queryKey = useMemo(
    () => [
      'userSetting',
      activeAccountWebClientConfig.activeAccountId,
      currentLoginUserOnly,
      id,
    ],
    [activeAccountWebClientConfig.activeAccountId, currentLoginUserOnly, id]
  );

  const shouldQuery = useMemo(
    () =>
      contextIsReady &&
      activeAccountWebClientConfig.activeAccountId != null &&
      id != null,
    [contextIsReady, activeAccountWebClientConfig.activeAccountId, id]
  );

  const result = useQuery<T | null>({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) return null;

      // ADD OVERRIDES HERE FOR SETTINGS WITH ENUMS VALUES
      switch (id) {
        case UserSetting.QuickFiltersCustomInventory:
          return (await sellerUserClient.getCustomInventoryQuickFiltersSetting()) as T;
        case UserSetting.QuickFiltersCustomSales:
          return (await sellerUserClient.getCustomSalesQuickFiltersSetting()) as T;
        case UserSetting.QuickFiltersCustomPurchases:
          return (await sellerUserClient.getCustomPurchasesQuickFiltersSetting()) as T;
        case UserSetting.QuickFiltersCustomMarketplacePayments:
          return (await sellerUserClient.getCustomMarketplacePaymentsQuickFiltersSetting()) as T;
        case UserSetting.QuickFiltersCustomOnSaleEvent:
          return (await sellerUserClient.getCustomOnSaleEventQuickFiltersSetting()) as T;
      }
      return defaultGetSellerUserSetting(currentLoginUserOnly);
    },

    enabled: shouldQuery,
    staleTime: Infinity, // We invalidate on user settings changed, so we don't need to make this stale
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('SellerUserClient.getSellerUserSetting', error, {
          trackErrorData: { id },
        });
      },
    },
  });

  const defaultGetSellerUserSetting = useCallback(
    async (currentLoginUserOnly?: boolean) => {
      if (!id) {
        return null;
      }

      if (isSettingPrefetched?.(id)) {
        return null;
      }

      const resp = await sellerUserClient.getSellerUserSettings(
        [id],
        currentLoginUserOnly ?? false
      );

      const userSettingValue = resp[id];

      return userSettingValue == null
        ? null
        : (JSON.parse(userSettingValue) satisfies T);
    },
    [id, isSettingPrefetched, sellerUserClient]
  );

  const defaultSetSellerUserSetting = useCallback(
    async (value: T, currentLoginUserOnly?: boolean) => {
      return await sellerUserClient.setSellerUserSetting(
        id,
        JSON.stringify(value),
        currentLoginUserOnly ?? true
      );
    },
    [id, sellerUserClient]
  );

  const queryClient = useQueryClient();

  const setUserSettingRef = useRef({
    isSettingPrefetched,
    defaultSetSellerUserSetting,
    prefetchRefresh,
    prefetchQueryKey,
  });
  setUserSettingRef.current = {
    isSettingPrefetched,
    defaultSetSellerUserSetting,
    prefetchRefresh,
    prefetchQueryKey,
  };

  const setUserSetting = useCallback(
    (value: T) => {
      const {
        isSettingPrefetched,
        defaultSetSellerUserSetting,
        prefetchRefresh,
        prefetchQueryKey,
      } = setUserSettingRef.current;

      if (!id) {
        return Promise.resolve(false);
      }

      if (isSettingPrefetched?.(id)) {
        queryClient.setQueryData(prefetchQueryKey, (prev: object) => ({
          ...(prev ?? {}),
          [id]: JSON.stringify(value),
        }));
      } else {
        queryClient.setQueryData(queryKey, value);
      }

      return tryInvokeApi(
        async () => {
          if (activeAccountWebClientConfig.activeAccountId == null) return;

          let success = false;
          // ADD OVERRIDES HERE FOR SETTINGS WITH ENUMS VALUES
          switch (id) {
            case UserSetting.QuickFiltersCustomInventory:
              success =
                await sellerUserClient.setCustomInventoryQuickFiltersSetting(
                  value as CustomQuickFilterOfListingQuery[]
                );
              break;
            case UserSetting.QuickFiltersCustomSales:
              success =
                await sellerUserClient.setCustomSalesQuickFiltersSetting(
                  value as CustomQuickFilterOfSaleQuery[]
                );
              break;
            case UserSetting.QuickFiltersCustomPurchases:
              success =
                await sellerUserClient.setCustomPurchasesQuickFiltersSetting(
                  value as CustomQuickFilterOfPurchaseOrderQuery[]
                );
              break;
            case UserSetting.QuickFiltersCustomMarketplacePayments:
              success =
                await sellerUserClient.setCustomMarketplacePaymentsQuickFiltersSetting(
                  value as CustomQuickFilterOfMarketplacePaymentQuery[]
                );
              break;
            case UserSetting.QuickFiltersCustomOnSaleEvent:
              success =
                await sellerUserClient.setCustomOnSaleEventQuickFiltersSetting(
                  value as CustomQuickFilterOfOnSaleEventQuery[]
                );
              break;
            default:
              success = await defaultSetSellerUserSetting(
                value,
                currentLoginUserOnly
              );
              break;
          }

          if (success) {
            if (isSettingPrefetched?.(id)) {
              prefetchRefresh?.();
            } else {
              queryClient.invalidateQueries({ queryKey: queryKey });
            }
          }
        },
        (error) => {
          showErrorDialog('onSubmitChangePrice', error, {
            trackErrorData: {
              userSettings: queryKey,
            },
          });
        }
      );
    },
    [
      activeAccountWebClientConfig.activeAccountId,
      currentLoginUserOnly,
      id,
      queryClient,
      queryKey,
      sellerUserClient,
      showErrorDialog,
    ]
  );

  return useMemo(
    () => ({
      data: result.data ?? undefined,
      isLoading: result.isLoading,
      setUserSetting,
    }),
    [result.data, result.isLoading, setUserSetting]
  );
};

/**
 * Feature that is visible to the user for the first time.
 */
export enum FirstTimeFeature {
  EventMapBanner = 'EventMapBanner',
}

/**
 * Generic type for a `useUserSetting` hook.
 *
 * This can be implemented with different storage solutions.
 */
export type UseUserSettingHook = <T>(options: {
  id?: UserSetting;
  currentLoginUserOnly?: boolean;
}) => {
  value: T | undefined;
  setUserSetting: (value: T) => Promise<unknown>;
  isLoading: boolean;
};

/**
 * The original userServerUserSetting hook.
 * TODO: Remove when FEPerf_ReworkUserSettingsContext is GA'ed
 */
const useServerUserSettingFromContext: UseUserSettingHook = <T>({
  id,
  currentLoginUserOnly = true,
}: {
  id?: UserSetting;
  /* whether to use the active account's user or the login user (which includes impersonating user) Id */
  currentLoginUserOnly?: boolean;
}) => {
  // Try to use the seller user settings context if possible
  const newContext = useContext(SellerUserAllSettingsContext);

  const prefetchUserSettingIds = useMemo(
    () => newContext?.contextSettingIds ?? [],
    [newContext?.contextSettingIds]
  );

  const prefetchUserSettingsResult = useMemo(
    () => newContext?.queryResult,
    [newContext?.queryResult]
  );

  const prefetchQueryKey = useMemo(
    () => newContext?.contextQueryKey ?? [],
    [newContext?.contextQueryKey]
  );

  const prefetchIsLoading = useMemo(
    () => newContext?.isLoading ?? false,
    [newContext?.isLoading]
  );

  const prefetchRefresh = useMemo(
    () => newContext?.refresh,
    [newContext?.refresh]
  );

  const contextIsReady = useMemo(
    () => prefetchUserSettingsResult != null && !prefetchIsLoading,
    [prefetchIsLoading, prefetchUserSettingsResult]
  );
  const isSettingPrefetched = useCallback(
    (id: UserSetting) => Boolean(prefetchUserSettingIds?.includes(id)),
    [prefetchUserSettingIds]
  );
  const { data, isLoading, setUserSetting } = useRealTimeUserSetting<T>(
    prefetchQueryKey,
    contextIsReady,
    id,
    currentLoginUserOnly,
    isSettingPrefetched,
    prefetchRefresh
  );

  const value = useMemo(() => {
    if (id && prefetchUserSettingIds.includes(id)) {
      const userSettingValue = prefetchUserSettingsResult?.[id];

      return userSettingValue == null
        ? undefined
        : (JSON.parse(userSettingValue) satisfies T);
    }

    return data ?? undefined;
  }, [id, prefetchUserSettingIds, prefetchUserSettingsResult, data]);

  return {
    value,
    setUserSetting,
    isLoading,
  };
};

export const useUserSettingAtom: UseUserSettingHook = <T>({
  id,
  currentLoginUserOnly = true,
}: {
  id?: UserSetting;
  /* whether to use the active account's user or the login user (which includes impersonating user) Id */
  currentLoginUserOnly?: boolean;
}) => {
  const prefetchQueryKey = useAtomValue(userSettingsQueryKeyAtom);
  const userSettingValue = useAtomValue(userSettingsFamilyAtom(id));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryClient = useQueryClient();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const prefetchRefresh = useCallback(
    debounce(() => {
      queryClient.invalidateQueries({
        queryKey: prefetchQueryKey,
      });
    }, 500), // 500ms debounce so bunch of saves to user settings don't trigger a ton of invalidations
    [queryClient, prefetchQueryKey]
  );

  // Standard pattern to avoid recreating the isSettingPrefetched function on every render,
  const userSettingValueRef = useRef(userSettingValue);
  userSettingValueRef.current = userSettingValue;
  const contextIsReady = useMemo(
    () => userSettingValueRef.current !== null,
    [] // Empty deps - uses ref to avoid recreation
  );

  const isSettingPrefetched = useCallback(
    (id: UserSetting) => userSettingValueRef.current !== undefined,
    [] // Empty deps - uses ref to avoid recreation
  );
  const { data, isLoading, setUserSetting } = useRealTimeUserSetting<T>(
    prefetchQueryKey,
    contextIsReady,
    id,
    currentLoginUserOnly,
    isSettingPrefetched,
    prefetchRefresh
  );

  const value = useMemo(() => {
    if (id && isSettingPrefetched(id)) {
      return userSettingValue == null
        ? undefined
        : (JSON.parse(userSettingValue) satisfies T);
    }

    return data ?? undefined;
  }, [id, isSettingPrefetched, data, userSettingValue]);

  return useMemo(
    () => ({
      value,
      setUserSetting,
      isLoading,
    }),
    [value, setUserSetting, isLoading]
  );
};

export const useServerUserSetting: UseUserSettingHook = <T>(options: {
  id?: UserSetting;
  currentLoginUserOnly?: boolean;
}) => {
  const hasReworkUserSettingsContextFeature = useUserHasFeature(
    Feature.FEPerf_ReworkUserSettingsContext
  );
  const contextSettings = useServerUserSettingFromContext<T>(options);
  const atomSettings = useUserSettingAtom<T>(options);

  return hasReworkUserSettingsContextFeature ? atomSettings : contextSettings;
};
